import { Facebook, Instagram, LinkedIn, Twitter, Web } from '@air/icons';
import { Box, Text } from '@air/zephyr';

import { AgencyCardImage } from '~/old-marketing/components/agency/AgencyCard/AgencyCardImage';
import { AgencyCardSocialIconButton } from '~/old-marketing/components/agency/AgencyCard/AgencyCardSocialIconButton';
import { AgencyCardTitle } from '~/old-marketing/components/agency/AgencyCard/AgencyCardTitle';
import { ImageData, LimitedAgencyData } from '~/old-marketing/components/types/custom-types';
import { formatAgencyTypes } from '~/old-marketing/utils/formatAgencyTypes';

export interface AgencyCardProps extends Omit<LimitedAgencyData, 'companySize' | 'id' | 'location' | 'logos'> {
  image?: ImageData;
}

export const AgencyCard = ({
  image,
  facebookUrl,
  instagramUrl,
  linkedInUrl,
  name,
  slug,
  twitterUrl,
  websiteUrl,
  types,
}: AgencyCardProps) => {
  const hasSocialIcons = facebookUrl || instagramUrl || linkedInUrl || twitterUrl || websiteUrl;

  if (!slug) return null;

  return (
    <Box
      as="article"
      tx={{
        position: 'relative',
        p: 12,
        border: '1px solid',
        borderColor: 'pigeon100',
        borderRadius: 4,
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background-color: 0.2s ease',

        '&:hover': {
          borderColor: 'transparent',
          boxShadow: `
            0px 2px 8px rgba(0, 0, 0, 0.2), 
            0px 1px 3px rgba(0, 0, 0, 0.15), 
            0px 0px 2px rgba(0, 0, 0, 0.25)
          `,
        },

        '&:active': {
          backgroundColor: 'pigeon025',
          borderColor: 'pigeon100',
          boxShadow: 'none',
        },
      }}
    >
      <AgencyCardImage url={image?.url} />

      <AgencyCardTitle slug={slug}>{name}</AgencyCardTitle>

      <Box tx={{ zIndex: 1, position: 'relative' }}>
        {types?.length > 0 && (
          <Text as="p" tx={{ color: 'pigeon600' }} variant="text-ui-14">
            {formatAgencyTypes(types)}
          </Text>
        )}

        {hasSocialIcons && (
          <Box tx={{ display: 'flex', mt: 8, ml: -4 }}>
            <AgencyCardSocialIconButton href={websiteUrl} icon={Web}>
              {`${name} website`}
            </AgencyCardSocialIconButton>

            <AgencyCardSocialIconButton href={facebookUrl} icon={Facebook}>
              Facebook
            </AgencyCardSocialIconButton>

            <AgencyCardSocialIconButton href={twitterUrl} icon={Twitter}>
              Twitter
            </AgencyCardSocialIconButton>

            <AgencyCardSocialIconButton href={linkedInUrl} icon={LinkedIn}>
              LinkedIn
            </AgencyCardSocialIconButton>

            <AgencyCardSocialIconButton href={instagramUrl} icon={Instagram}>
              Instagram
            </AgencyCardSocialIconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};
